/**
 * MESSAGE API — Wrapper REST Liferay allineato a MessagesResourceImpl.java.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /messages/{groupId}/{direction}  pagination server-side  -> Page<Message>
 *     direction: segmento di path obbligatorio (IN | OUT), default IN.
 *     Query params: page (1-based, default 1), pageSize (default 20),
 *                   sort (es. "receiptDate:desc"), search.
 *   - GET    /message/{messageId}                                 -> Message
 *   - GET    /message/{messageId}/summary                         -> MessageSummary (proiezione
 *     leggera per la timeline attività: oggetto/direzione/data/mittente, niente body/infos/allegati)
 *   - PATCH  /message/{messageId}/label  MessageLabelInput        -> Message
 *
 * I messaggi nascono solo da import IMAP: niente create/delete lato CRM e il
 * contenuto della mail e' immutabile (l'unico campo aggiornabile e' il label,
 * stato di lavorazione, via PATCH /message/{messageId}/label). Gli endpoint
 * POST /messages e DELETE /message sono stati rimossi dal backend; i metodi
 * create/remove qui sotto sono stub che lanciano (vedi TODO).
 *
 * NB: singolare per get/update, plurale per list.
 * NB: param di ricerca e' `search` (non `q`).
 * NB: `label` e' un int32 server-side (enum MessageLabel backend);
 *     qui il payload UI accetta la union string MessageLabel e il
 *     mapper (messageMappers) converte in int verso il DTO.
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import {
  messageFromDto,
  messageLabelToInput,
  messageLabelToInt,
  type MessageDto,
} from './messageMappers';
import type { Message, MessageDirection, MessageLabel, MessageSummary } from './messageTypes';

const COLLECTION = '/messages';
const SINGLE = '/message';

export interface MessageListParams extends ServerListParams {
  /** Direzione: segmento di path obbligatorio lato backend (IN | OUT). Default IN se omessa. */
  direction?: MessageDirection;
  /**
   * Filtro per stato di lavorazione. Omesso = gli stati da lavorare (il backend
   * senza `label` filtra su TO_PROCESS, IN_PROGRESS ed ERROR). Union string lato UI,
   * convertito in int verso il DTO (enum MessageLabel backend).
   */
  label?: MessageLabel;
}

export interface MessageCreatePayload {
  subject: string;
  body: string;
  senderName: string;
  senderEmailAddress: string;
  receiptDate?: string | null;
  label?: MessageLabel | null;
}

export interface MessageUpdatePayload {
  messageId: number;
  label: MessageLabel;
}

export const messageApi = {
  getAll: (groupId: number, params?: MessageListParams): Promise<Page<Message>> => {
    // direction e' un segmento di path obbligatorio lato backend (IN | OUT, default IN), non un
    // query param: lo si estrae da params e si manda il resto come query string.
    const { direction = 'IN', label, ...query } = params ?? {};
    // `label` sul filo è l'int dell'enum backend; omesso (tab "Tutte") il backend
    // restituisce gli stati da lavorare: TO_PROCESS, IN_PROGRESS ed ERROR.
    const queryParams: Record<string, unknown> = { ...query };
    if (label !== undefined) queryParams.label = messageLabelToInt(label);
    return httpClient
      .get<Page<MessageDto>>(`${COLLECTION}/${groupId}/${direction}`, {
        params: queryParams,
      })
      .then((r) => ({
        ...r.data,
        items: r.data.items.map(messageFromDto),
      }));
  },

  getById: (messageId: number): Promise<Message> =>
    httpClient
      .get<MessageDto>(`${SINGLE}/${messageId}`)
      .then((r) => messageFromDto(r.data)),

  // Proiezione leggera per la timeline attività (GET /message/{id}/summary): solo oggetto,
  // direzione, data, mittente. Nessuna conversione (il DTO mappa 1:1 MessageSummary): direction è
  // già 'IN'/'OUT', receiptDate già stringa ISO. Molto più economico di getById.
  getSummary: (messageId: number): Promise<MessageSummary> =>
    httpClient
      .get<MessageSummary>(`${SINGLE}/${messageId}/summary`)
      .then((r) => r.data),

  // Scarica il contenuto binario di un allegato (GET .../content -> octet-stream).
  // Il backend valida l'associazione utente-casella e la coerenza messageId/allegato;
  // qui ritorniamo il solo Blob, il caller lo salva su disco (triggerBrowserDownload).
  downloadAttachment: (messageId: number, messageAttachmentId: number): Promise<Blob> =>
    httpClient
      .getBlob(`${SINGLE}/${messageId}/attachment/${messageAttachmentId}/content`)
      .then((r) => r.data),

  // TODO infrastruttura morta (refactor a Pattern 7 — CRUD parziale). I Message
  // nascono solo da import IMAP, non si creano da CRM e l'endpoint POST è stato
  // rimosso dal backend. Questo stub resta solo per soddisfare il contratto
  // CrudApi di createCrudThunks; nessuna UI lo usa. Quando il flusso
  // Message/Mailbox sarà stabile: rimuovere create/remove + i thunk
  // createMessage/deleteMessage + MessageCreatePayload + messageCreateToInput +
  // i mock POST/DELETE, e convertire messageThunks/messageSlice a CRUD parziale
  // (fetchAll/fetchOne/update) senza createCrudThunks/addCrudCases.
  create: (_groupId: number, _payload: MessageCreatePayload): Promise<Message> => {
    throw new Error(
      'Operazione non supportata: i messaggi nascono solo da import IMAP, non si creano da CRM',
    );
  },

  update: (payload: MessageUpdatePayload): Promise<Message> => {
    const { messageId, label } = payload;
    return httpClient
      .patch<MessageDto>(`${SINGLE}/${messageId}/label`, messageLabelToInput(label))
      .then((r) => messageFromDto(r.data));
  },

  // TODO infrastruttura morta — vedi nota su create(): stub che lancia, l'endpoint
  // DELETE /message è stato rimosso dal backend. Da rimuovere col refactor Pattern 7.
  remove: (_messageId: number): Promise<void> => {
    throw new Error('Operazione non supportata: i messaggi non si cancellano da CRM');
  },
};
